import {formatPhoneNumber} from 'platform/locale';

import {isNil, isNotNil, reject, uniqBy} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {
  CustomerResponseBodyV2,
  PersonResponseBodyV2,
  PhoneNumberResponseBody,
} from '@dms/api/customer';

import {Nullish} from 'shared/web';

export interface CustomerCallPhoneNumber {
  /** Dialable number as `prefix + number`; the backend normalises it to E.164. */
  value: string;
  /** Formatted number followed by its type and, for contact persons, the person's name. */
  label: string;
}

const SEPARATOR = ' · ';

const toDialable = (phone: PhoneNumberResponseBody) =>
  isNotNilOrEmpty(phone.prefix) && isNotNilOrEmpty(phone.number)
    ? `${phone.prefix}${phone.number}`
    : null;

const getPersonName = (person: PersonResponseBodyV2) =>
  [person.firstName, person.lastName].filter(isNotNil).join(' ');

const toOption = (
  phone: PhoneNumberResponseBody,
  owner: PersonResponseBodyV2 | Nullish
): CustomerCallPhoneNumber | null => {
  const value = toDialable(phone);

  if (isNil(value)) {
    return null;
  }

  const ownerName = isNotNil(owner) ? getPersonName(owner) : '';
  const details = [phone.type, ownerName].filter(isNotNilOrEmpty);

  return {value, label: [formatPhoneNumber(value), ...details].join(SEPARATOR)};
};

/**
 * Every number the customer can be called on, in the same order the SMS form offers them: the
 * founding person first for natural persons, then the contact persons. Duplicates are dropped.
 */
export const getCustomerCallPhoneNumbers = (
  customer: CustomerResponseBodyV2 | Nullish
): CustomerCallPhoneNumber[] => {
  if (isNil(customer)) {
    return [];
  }

  const foundingPersonNumbers =
    customer.discriminator === 'BUSINESS'
      ? []
      : (customer.foundingPerson?.phoneNumbers ?? []).map((phone) => toOption(phone, null));

  const contactNumbers = customer.contacts.flatMap((contact) =>
    contact.phoneNumbers.map((phone) => toOption(phone, contact))
  );

  return uniqBy(
    (option) => option.value,
    reject(isNil, [...foundingPersonNumbers, ...contactNumbers])
  );
};
